const controlFramework = (control) => control?.type || control?.framework;

export const groupControlIdsByFramework = (controls = []) => {
    return controls.reduce((groups, control) => {
        const framework = controlFramework(control);

        if (!framework || control?.id == null) {
            return groups;
        }

        return {
            ...groups,
            [framework]: [...(groups[framework] || []), control.id],
        };
    }, {});
};

/**
 * Sends every selected id, and keeps linked controls whose framework
 * list did not load so a partial picker cannot drop them.
 */
export const buildReplaceControlsPayload = ({
    selectedByFramework = {},
    linkedControls = [],
    loadedFrameworks = [],
} = {}) => {
    const loaded = new Set(loadedFrameworks);
    const preservedIds = linkedControls
        .filter((control) => (
            control?.id != null && !loaded.has(controlFramework(control))
        ))
        .map((control) => Number(control.id));
    const selectedIds = Object.values(selectedByFramework)
        .flat()
        .map((id) => Number(id));

    const controlIds = [...preservedIds, ...selectedIds].filter((id) => (
        Number.isFinite(id)
    ));

    return {
        control_ids: [...new Set(controlIds)],
    };
};

export const buildSelectedControls = ({
    selectedByFramework = {},
    optionsByFramework = {},
    linkedControls = [],
    loadedFrameworks = [],
} = {}) => {
    const loaded = new Set(loadedFrameworks);
    const byId = new Map();

    linkedControls.forEach((control) => {
        const framework = controlFramework(control);

        if (control?.id != null && !loaded.has(framework)) {
            byId.set(String(control.id), control);
        }
    });

    Object.entries(selectedByFramework).forEach(([framework, ids]) => {
        const options = optionsByFramework[framework] || [];

        (ids || []).forEach((id) => {
            const option = options.find((item) => String(item.value) === String(id));
            const linked = linkedControls.find((control) => (
                String(control.id) === String(id)
            ));
            const source = option?.control || linked || {};

            byId.set(String(id), {
                ...source,
                id: source.id ?? id,
                type: controlFramework(source) || framework,
                title: source.title || "",
                control_number: source.control_number || source.controlNumber || "",
            });
        });
    });

    return [...byId.values()];
};

const sameControlIds = (controls, controlIds) => {
    const savedIds = controls.map((control) => String(control.id)).sort();
    const requestedIds = controlIds.map((id) => String(id)).sort();

    return savedIds.length === requestedIds.length
        && savedIds.every((id, index) => id === requestedIds[index]);
};

export const resolveSavedControls = (measure, selectedControls, controlIds) => {
    const responseControls = Array.isArray(measure?.controls) ? measure.controls : null;
    const requestedIds = Array.isArray(controlIds) ? controlIds : null;

    if (responseControls && requestedIds && sameControlIds(responseControls, requestedIds)) {
        return responseControls;
    }

    if (Array.isArray(selectedControls)) {
        return selectedControls;
    }

    return responseControls;
};
